import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, homeFor, ROLE_LABEL } from "@/lib/api";
import {
  BarChart3, Building2, CalendarClock, CalendarDays, Car, ChevronDown, ClipboardList, Clock, FolderOpen, Headset, KeyRound, ScanFace,
  LayoutDashboard, LifeBuoy, LogOut, MapPin, Megaphone, Menu, Phone, PlusCircle, ScrollText, Settings, Ticket, Users,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { ThemeToggle } from "@/components/ThemeToggle";
import TenantSettingsDialog from "@/components/TenantSettingsDialog";
import EmergencyMonitor from "@/components/EmergencyMonitor";
import { toast } from "sonner";

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/tenants", label: "Tenants", icon: Building2, testid: "nav-tenants" },
];

const TENANT_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/chamados", label: "Chamados", icon: Ticket, testid: "nav-chamados", badgeKey: "chamados", showZero: true },
  { to: "/app/mural", label: "Mural de Avisos", icon: Megaphone, testid: "nav-mural", badgeKey: "mural" },
  { to: "/app/manifestacoes", label: "Pedidos e Ocorrências", icon: ClipboardList, testid: "nav-manifestacoes", resetManifestFilters: true },
  { to: "/app/reservas", label: "Reservas", icon: CalendarDays, testid: "nav-reservas", badgeKey: "reservas", resetStatusFilter: true },
  { to: "/app/convites-acesso", label: "Convites de Acesso", icon: KeyRound, testid: "nav-convites-acesso" },
  { to: "/app/agendamentos", label: "Agendamentos", icon: CalendarClock, testid: "nav-agendamentos" },
  { to: "/recordweb", label: "Telefone Portaria", icon: Phone, testid: "nav-recordweb" },
  {
    group: "Cadastros", icon: FolderOpen, testid: "nav-cadastros",
    children: [
      { to: "/app/horarios", label: "Horários", icon: Clock, testid: "nav-horarios" },
      { to: "/app/areas", label: "Áreas e Locais", icon: MapPin, testid: "nav-areas" },
      { action: "brave-sso", label: "Pessoas e Veículos", icon: Car, testid: "nav-pessoas-veiculos" },
      { to: "/app/equipe", label: "Equipe & Clientes", icon: Users, testid: "nav-equipe" },
    ],
  },
  {
    group: "Relatórios", icon: BarChart3, testid: "nav-relatorios",
    children: [
      { to: "/app/relatorios/pendencias", label: "Pendências", icon: Clock, testid: "nav-relatorios-pendencias" },
      { to: "/app/relatorios/reservas", label: "Reservas", icon: CalendarDays, testid: "nav-relatorios-reservas" },
      { to: "/app/relatorios/auditoria", label: "Auditoria", icon: ScrollText, testid: "nav-relatorios-auditoria" },
      { to: "/app/relatorios/facial", label: "Reconhecimento Facial", icon: ScanFace, testid: "nav-relatorios-facial" },
    ],
  },
  { to: "/app/integracoes", label: "Integrações & API", icon: KeyRound, testid: "nav-integracoes", roles: ["tenant_admin"] },
];

function Brand({ role }) {
  return (
    <>
      {role === "super_admin" ? <Headset className="h-5 w-5 text-indigo-400" /> : <LifeBuoy className="h-5 w-5 text-indigo-400" />}
      <span className="font-semibold text-zinc-100 tracking-tight">{role === "super_admin" ? "InterDesk" : "Help Desk"}</span>
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
        {role === "super_admin" ? "Admin" : "Tenant"}
      </span>
    </>
  );
}

function SideNav({ items, onNavigate, suffix = "", collapsed = false, badges = {}, onAction }) {
  const [openGroups, setOpenGroups] = useState({});

  const renderLink = (item, child = false) => {
    const { to, label, icon: Icon, end, testid, badgeKey, showZero, action } = item;
    if (action) {
      return (
        <button key={testid} type="button" data-testid={`${testid}${suffix}`}
          title={collapsed ? label : undefined}
          onClick={() => { onAction?.(action); onNavigate?.(); }}
          className={`w-full flex items-center ${collapsed ? "justify-center px-0" : `gap-3 px-3 ${child ? "pl-8" : ""}`} py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100 transition-colors duration-200`}>
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && label}
        </button>
      );
    }
    return (
      <NavLink
        key={to} to={to} end={end}
        onClick={() => {
          if (item.resetStatusFilter) {
            try {
              const saved = JSON.parse(sessionStorage.getItem("reservas-filters") || "{}");
              sessionStorage.setItem("reservas-filters", JSON.stringify({ ...saved, status: "confirmada" }));
            } catch {}
            window.dispatchEvent(new CustomEvent("reservas:reset-status"));
          }
          if (item.resetManifestFilters) {
            window.dispatchEvent(new CustomEvent("manifestacoes:reset-filters"));
          }
          onNavigate?.();
        }}
        data-testid={`${testid}${suffix}`}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          `flex items-center ${collapsed ? "justify-center px-0" : `gap-3 px-3 ${child ? "pl-8" : ""}`} py-2 rounded-md text-sm transition-colors duration-200 ${
            isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"
          }`
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && label}
        {!collapsed && badgeKey && (showZero ? badges[badgeKey] != null : badges[badgeKey] > 0) && (
          <span data-testid={`${testid}-badge${suffix}`}
            className="ml-auto text-[10px] font-semibold bg-indigo-600/25 text-indigo-300 rounded-full px-1.5 py-0.5 leading-none">
            {badges[badgeKey]}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {items.map((item) => {
        if (!item.group) return renderLink(item);
        if (collapsed) return item.children.map((c) => renderLink(c));
        const open = openGroups[item.group] ?? false;
        const GroupIcon = item.icon;
        return (
          <div key={item.group}>
            <button type="button" data-testid={`${item.testid}${suffix}`}
              onClick={() => setOpenGroups({ ...openGroups, [item.group]: !open })}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100 transition-colors duration-200">
              <GroupIcon className="h-4 w-4 shrink-0" />
              {item.group}
              <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
            </button>
            {open && <div className="mt-0.5 space-y-1">{item.children.map((c) => renderLink(c, true))}</div>}
          </div>
        );
      })}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("sidebar-collapsed", c ? "0" : "1");
      return !c;
    });
  };
  const isClient = user?.role === "client";
  const baseNav = user?.role === "super_admin" ? ADMIN_NAV : TENANT_NAV;
  const nav = baseNav.filter((i) => !i.roles || i.roles.includes(user?.role));
  const location = useLocation();
  const [muralCount, setMuralCount] = useState(0);
  const [chamadosCount, setChamadosCount] = useState(null);
  const [reservasCount, setReservasCount] = useState(0);

  useEffect(() => {
    if (!user || user.role === "super_admin") return undefined;
    const endpoint = user.role === "client" ? "/tenant/notices/board" : "/tenant/notices";
    let cancelled = false;
    api.get(endpoint).then(({ data }) => {
      if (cancelled) return;
      const now = new Date();
      setMuralCount(data.filter((n) => n.active && new Date(n.valid_from) <= now && now <= new Date(n.valid_until)).length);
    }).catch(() => {});
    if (user.role !== "client") {
      api.get("/tickets/stats").then(({ data }) => {
        if (cancelled) return;
        const open = Object.entries(data.by_status || {})
          .filter(([k]) => k !== "fechado")
          .reduce((acc, [, v]) => acc + v, 0);
        setChamadosCount(open);
      }).catch(() => {});
      api.get("/tenant/reservas/stats").then(({ data }) => {
        if (cancelled) return;
        setReservasCount(data.ativas || 0);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [location.pathname, user]);

  const handleNavAction = async (action) => {
    if (action !== "brave-sso") return;
    try {
      const { data } = await api.post("/tenant/brave/sso-link");
      window.open(data.sso_url, "brave-access");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const userBlock = (btnTestId) => (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="bg-indigo-600/20 text-indigo-300 text-xs">{initials(user?.name)}</AvatarFallback>
      </Avatar>
      <button type="button" data-testid={btnTestId === "logout-button-bottom" ? "open-change-password-button-mobile" : "open-change-password-button"} onClick={() => setPwOpen(true)}
        title="Minha conta (e-mail e senha)"
        className="min-w-0 text-left rounded-md px-1.5 py-1 -mx-1.5 hover:bg-zinc-800/60 transition-colors duration-200">
        <p className="text-sm font-medium text-zinc-100 truncate" data-testid="current-user-name">{user?.name}</p>
        <p className="text-[11px] text-zinc-400">{ROLE_LABEL[user?.role]}</p>
        {user?.tenant_name && (
          <p className="text-[11px] text-zinc-500 truncate" data-testid="tenant-short-name" title={user.tenant_name}>
            {user.tenant_name.split(" ").slice(0, 2).join(" ")}
          </p>
        )}
      </button>
      <Button data-testid={btnTestId} variant="ghost" size="icon" onClick={doLogout}
        className="ml-auto text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 shrink-0">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );

  const mobileUserBar = (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="bg-indigo-600/20 text-indigo-300 text-xs">{initials(user?.name)}</AvatarFallback>
      </Avatar>
      <button type="button" data-testid="open-change-password-button-mobile" onClick={() => setPwOpen(true)}
        title="Minha conta (e-mail e senha)"
        className="min-w-0 text-left rounded-md px-1.5 py-1 -mx-1.5 hover:bg-zinc-800/60 transition-colors duration-200">
        <p className="text-sm font-medium text-zinc-100 truncate" data-testid="current-user-name-mobile">{user?.name}</p>
      </button>
      <Button data-testid="logout-button-bottom" variant="ghost" size="icon" onClick={doLogout}
        className="ml-auto text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 shrink-0">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );

  if (isClient) {
    return (
      <div className="min-h-screen">
        <header className="dark sticky top-0 z-40 bg-[#0A0E1A]/80 backdrop-blur-md border-b border-[#1E2B45]/60">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
            <Link to="/portal" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
              <LifeBuoy className="h-5 w-5 text-indigo-400" />
              <span className="font-semibold text-zinc-100 tracking-tight">Help Desk</span>
            </Link>
            <nav className="flex items-center gap-1">
              <NavLink to="/portal" end data-testid="nav-meus-chamados"
                className={({ isActive }) => `px-2.5 py-1.5 rounded-md text-sm transition-colors duration-200 ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-100"}`}>
                Chamados
              </NavLink>
              <NavLink to="/portal/novo" data-testid="nav-novo-chamado"
                className={({ isActive }) => `px-2.5 py-1.5 rounded-md text-sm transition-colors duration-200 flex items-center gap-1.5 ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-100"}`}>
                <PlusCircle className="h-3.5 w-3.5" /> Novo
              </NavLink>
              <NavLink to="/portal/mural" data-testid="nav-mural-client"
                className={({ isActive }) => `px-2.5 py-1.5 rounded-md text-sm transition-colors duration-200 flex items-center gap-1.5 ${isActive ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-100"}`}>
                Mural
                {muralCount > 0 && (
                  <span data-testid="nav-mural-badge-client"
                    className="text-[10px] font-semibold bg-indigo-600/25 text-indigo-300 rounded-full px-1.5 py-0.5 leading-none">
                    {muralCount}
                  </span>
                )}
              </NavLink>
            </nav>
            <div className="ml-auto flex items-center justify-end gap-1 w-10 sm:w-64">
              <ThemeToggle tid="theme-toggle-client" />
              <div className="hidden sm:block">{userBlock("logout-button")}</div>
              <Button data-testid="logout-button-mobile" variant="ghost" size="icon" onClick={doLogout}
                className="sm:hidden text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8"><Outlet /></main>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        {user?.must_change_password && <ForcePasswordChange />}
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="dark lg:hidden sticky top-0 z-40 bg-[#0A0E1A]/80 backdrop-blur-md border-b border-[#1E2B45]/60 h-14 flex items-center px-4 gap-3">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button data-testid="mobile-menu-button" variant="ghost" size="icon"
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="dark bg-[#090D16]/95 backdrop-blur-xl border-[#1E2B45]/60 w-64 p-0 flex flex-col">
            <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
            <SheetDescription className="sr-only">Navegação principal do painel</SheetDescription>
            <div className="flex items-center gap-2 px-5 h-14 border-b border-zinc-800">
              <Brand role={user?.role} />
            </div>
            <SideNav items={nav} onNavigate={() => setMobileOpen(false)} suffix="-mobile" badges={{ mural: muralCount, chamados: chamadosCount, reservas: reservasCount }} onAction={handleNavAction} />
            {user?.role === "tenant_admin" && (
              <div className="px-3 pb-3">
                <button type="button" data-testid="settings-button-mobile"
                  onClick={() => { setMobileOpen(false); setSettingsOpen(true); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100 transition-colors duration-200">
                  <Settings className="h-4 w-4 shrink-0" /> Configurações
                </button>
              </div>
            )}
          </SheetContent>
        </Sheet>
        <Link to={homeFor(user)} className="flex items-center gap-2" data-testid="brand-link-mobile">
          <Brand role={user?.role} />
        </Link>
      </header>

      <aside className={`dark hidden lg:flex ${collapsed ? "w-16" : "w-60"} shrink-0 bg-[#090D16]/90 backdrop-blur-xl border-r border-[#1E2B45]/60 flex-col fixed inset-y-0 z-40 transition-[width] duration-200`}>
        <div className={`flex items-center ${collapsed ? "justify-center px-2" : "justify-between pl-5 pr-3"} h-14 border-b border-zinc-800`}>
          {!collapsed && (
            <Link to={homeFor(user)} className="flex items-center gap-2 shrink-0" data-testid="brand-link">
              <Brand role={user?.role} />
            </Link>
          )}
          <div className="flex items-center gap-1">
            <button type="button" data-testid="sidebar-collapse-toggle" onClick={toggleCollapsed}
              title={collapsed ? "Expandir menu" : "Recolher menu"}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md p-1.5 transition-colors duration-200">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        <SideNav items={nav} collapsed={collapsed} badges={{ mural: muralCount, chamados: chamadosCount, reservas: reservasCount }} onAction={handleNavAction} />
        {user?.role === "tenant_admin" && (
          <div className="px-3 pb-2">
            <button type="button" data-testid="settings-button" onClick={() => setSettingsOpen(true)}
              title="Configurações"
              className={`w-full flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100 transition-colors duration-200`}>
              <Settings className="h-4 w-4 shrink-0" />
              {!collapsed && "Configurações"}
            </button>
          </div>
        )}
        <div className={`${collapsed ? "p-2" : "p-4"} border-t border-zinc-800`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <button type="button" data-testid="open-change-password-button" onClick={() => setPwOpen(true)} title={user?.name}
                className="rounded-md hover:bg-zinc-800/60 p-1 transition-colors duration-200">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-indigo-600/20 text-indigo-300 text-xs">{initials(user?.name)}</AvatarFallback>
                </Avatar>
              </button>
              <Button data-testid="logout-button" variant="ghost" size="icon" onClick={doLogout} title="Sair"
                className="h-7 w-7 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : userBlock("logout-button")}
        </div>
      </aside>

      <main className={`${collapsed ? "lg:ml-16" : "lg:ml-60"} px-4 sm:px-8 pt-8 pb-24 lg:pb-8 max-w-6xl transition-[margin] duration-200`}><Outlet /></main>

      <div className="dark lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0E1A]/80 backdrop-blur-md border-t border-[#1E2B45]/60 px-4 py-2.5">
        {mobileUserBar}
      </div>
      <TenantSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <EmergencyMonitor />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      {user?.must_change_password && <ForcePasswordChange />}
    </div>
  );
}
